import React, { useState, useEffect, useRef, useContext } from "react";
import VN from "../components/VN";
import { UserDataContext } from "../components/UserDataContext";
import {
  Button,
  Card,
  CardContent,
  Container,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";

const LETTERS = ["A", "B", "C", "D", "E"];
const N = 2; // Poziom N-Back (2 kroki wstecz)
const ROUND_DURATION = 2000; // Czas wyświetlania jednej litery (ms)
const TOTAL_ROUNDS = 20; // Liczba liter w całej sesji

export default function NBackGame() {
  const [gameState, setGameState] = useState("START"); // START, PLAYING, RESULTS
  const [history, setHistory] = useState([]);
  const [currentLetter, setCurrentLetter] = useState("");
  const [roundCount, setRoundCount] = useState(0);
  const [score, setScore] = useState({
    correctMatches: 0,
    falseAlarms: 0,
    totalMatches: 0,
  });
  const [hasClickedThisRound, setHasClickedThisRound] = useState(false);
  const { userData } = useContext(UserDataContext);
  const timerRef = useRef(null);
  const historyRef = useRef([]);

  // Aktualizacja referencji historii, aby useEffect zawsze widział najnowszy stan
  useEffect(() => {
    historyRef.current = history;
  }, [history]);

  const startGame = () => {
    setGameState("PLAYING");
    setHistory([]);
    setRoundCount(0);
    setScore({ correctMatches: 0, falseAlarms: 0, totalMatches: 0 });
    setHasClickedThisRound(false);
    nextRound(0, []);
  };

  const nextRound = (currentRound, currentHistory) => {
    if (currentRound >= TOTAL_ROUNDS) {
      endGame();
      return;
    }

    let nextLet = LETTERS[Math.floor(Math.random() * LETTERS.length)];
    if (currentHistory.length >= N && Math.random() < 0.35) {
      nextLet = currentHistory[currentHistory.length - N];
    }

    const updatedHistory = [...currentHistory, nextLet];
    if (currentRound > 0 && !hasClickedThisRound) {
      const prevHistory = historyRef.current;
      if (
        prevHistory.length > N &&
        prevHistory[prevHistory.length - 1] ===
          prevHistory[prevHistory.length - 1 - N]
      ) {
        // Użytkownik przegapił dopasowanie (nie inkasuje punktu)
      }
    }

    // Liczenie wszystkich potencjalnych MAM! w grze
    if (
      updatedHistory.length > N &&
      nextLet === updatedHistory[updatedHistory.length - 1 - N]
    ) {
      setScore((prev) => ({ ...prev, totalMatches: prev.totalMatches + 1 }));
    }

    setCurrentLetter(nextLet);
    setHistory(updatedHistory);
    setRoundCount(currentRound + 1);
    setHasClickedThisRound(false);

    // Ustawienie timera na kolejną rundę
    timerRef.current = setTimeout(() => {
      nextRound(currentRound + 1, updatedHistory);
    }, ROUND_DURATION);
  };

  const handleMatchClick = () => {
    if (hasClickedThisRound || gameState !== "PLAYING") return;
    setHasClickedThisRound(true);

    const isMatch =
      history.length > N && currentLetter === history[history.length - 1 - N];

    if (isMatch) {
      setScore((prev) => ({
        ...prev,
        correctMatches: prev.correctMatches + 1,
      }));
    } else {
      setScore((prev) => ({ ...prev, falseAlarms: prev.falseAlarms + 1 }));
    }
  };

  const endGame = () => {
    clearTimeout(timerRef.current);
    setGameState("RESULTS");
  };

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  return (
    <div>
      <VN userData={userData} />
      <Container sx={{ py: 5 }}>
        <Card
          elevation={1}
          sx={{
            backgroundColor: "#F8FAFC",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#FFFFFF",
              textAlign: "center",
              pt: 4,
              pb: 2,
              px: 3,
            }}
          >
            <h1 style={{ color: "#333" }}>Trening Mózgu: 2-Back</h1>

            {gameState === "START" && (
              <div>
                <p
                  style={{ fontSize: "16px", color: "#666", lineHeight: "1.6" }}
                >
                  To wymagające zadanie na pamięć roboczą. Zobaczysz ciąg liter.{" "}
                  <br />
                  Kliknij przycisk <strong>„MAM!”</strong>, jeśli obecna litera
                  jest
                  <strong> identyczna jak ta sprzed 2 kroków</strong>.
                </p>
                <div
                  style={{
                    background: "#f5f5f5",
                    padding: "10px",
                    borderRadius: "5px",
                    margin: "20px 0",
                    fontSize: "14px",
                  }}
                >
                  Przykład: A → B → <strong>A</strong> (MAM!) → C →{" "}
                  <strong>B</strong> (MAM!)
                </div>
                <Button onClick={startGame} size="large">
                  Rozpocznij trening
                </Button>
              </div>
            )}

            {gameState === "PLAYING" && (
              <div>
                <p style={{ color: "#888" }}>
                  Postęp: {roundCount} / {TOTAL_ROUNDS}
                </p>

                <div
                  style={{
                    fontSize: "96px",
                    fontWeight: "bold",
                    margin: "40px 0",
                    color: "#222",
                    height: "120px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {currentLetter}
                </div>

                <button
                  onClick={handleMatchClick}
                  disabled={hasClickedThisRound || roundCount <= N}
                  style={{
                    ...buttonStyle,
                    backgroundColor: hasClickedThisRound ? "#ccc" : "#ff4d4f",
                    width: "100%",
                    fontSize: "22px",
                    cursor:
                      hasClickedThisRound || roundCount <= N
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  {hasClickedThisRound
                    ? "Zarejestrowano"
                    : "MAM! (2 kroki wstecz)"}
                </button>
              </div>
            )}

            {gameState === "RESULTS" && (
              <div>
                <h2 style={{ color: "#52c41a" }}>Trening zakończony!</h2>

                <div
                  style={{
                    margin: "20px 0",
                    textAlign: "left",
                    background: "#f5f5f5",
                    padding: "20px",
                    borderRadius: "8px",
                  }}
                >
                  <p>
                    <strong>Trafienia:</strong> {score.correctMatches} z{" "}
                    {score.totalMatches} możliwych
                  </p>
                  <p>
                    <strong>Fałszywe alarmy (pomyłki):</strong>{" "}
                    {score.falseAlarms}
                  </p>
                  <p
                    style={{
                      marginTop: "15px",
                      fontSize: "14px",
                      color: "#666",
                    }}
                  >
                    *Skuteczność mierzona jest liczbą prawidłowo wychwyconych
                    powtórzeń przy jak najmniejszej liczbie błędnych kliknięć.
                  </p>
                </div>

                <button onClick={startGame} style={buttonStyle}>
                  Trenuj ponownie
                </button>
              </div>
            )}
          </Box>
        </Card>
      </Container>
    </div>
  );
}

const buttonStyle = {
  backgroundColor: "#0070f3",
  color: "white",
  border: "none",
  padding: "12px 30px",
  fontSize: "18px",
  borderRadius: "5px",
  cursor: "pointer",
  fontWeight: "bold",
  boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
  transition: "background-color 0.2s",
};
